import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/shared/integrations/supabase/client";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { ClipboardList, ChevronRight, Calendar, Eye, GitMerge, Loader2, Plus } from "lucide-react";
import type { PropertyAttributeRow } from "@/features/properties/types/propertyAttributes";
import { createSurveyFromTemplate } from "../services/createSurveyFromTemplate";
import { MergeSurveysDialog } from "./MergeSurveysDialog";
import { TemplatePreviewDialog } from "./TemplatePreviewDialog";

interface SurveySelectorProps {
  projectId: string;
  propertyId: string;
  propertyName: string;
  onSelect: (survey: PropertyAttributeRow) => void;
}

// Use untyped alias since property_attributes isn't in generated types
const db = supabase as unknown as any;

export function SurveySelector({ projectId, propertyId, propertyName, onSelect }: SurveySelectorProps) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const { data: surveys, isLoading, error } = useQuery({
    queryKey: ["property-attributes-list", propertyId],
    queryFn: async (): Promise<PropertyAttributeRow[]> => {
      const { data, error } = await db
        .from("property_attributes")
        .select("id, property_id, captured_at, data, normalization_status, processing_events, last_attempt_at, attempt_count, ref_id")
        .eq("property_id", propertyId)
        .order("captured_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as PropertyAttributeRow[];
    },
    enabled: !!propertyId,
  });

  const { data: hasTemplate = false } = useQuery({
    queryKey: ["project-has-attribute-template", projectId],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await db
        .from("attribute_templates")
        .select("id")
        .eq("organization_id", projectId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const createSurvey = useMutation({
    mutationFn: () => createSurveyFromTemplate(projectId, propertyId),
    onSuccess: (newSurvey) => {
      queryClient.invalidateQueries({ queryKey: ["property-attributes-list", propertyId] });
      toast.success("Survey created");
      onSelect(newSurvey);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Failed to create survey");
    },
  });

  const actionButtons = (
    <div className="flex items-center gap-2">
      {(surveys?.length ?? 0) >= 2 && (
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          disabled={!hasTemplate}
          title={hasTemplate ? undefined : "No survey template configured for this project"}
          onClick={() => setMergeOpen(true)}
        >
          <GitMerge className="h-4 w-4" />
          Merge surveys
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        disabled={!hasTemplate}
        title={hasTemplate ? undefined : "No survey template configured for this project"}
        onClick={() => setPreviewOpen(true)}
      >
        <Eye className="h-4 w-4" />
        Preview template
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={!hasTemplate || createSurvey.isPending}
        title={hasTemplate ? undefined : "No survey template configured for this project"}
        onClick={() => setConfirmOpen(true)}
      >
        {createSurvey.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
        Start new survey
      </Button>
    </div>
  );

  const confirmDialog = (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start a new survey?</AlertDialogTitle>
          <AlertDialogDescription>
            This creates a new site survey for{" "}
            <span className="font-medium text-foreground">{propertyName}</span> from the
            project template. It becomes the latest survey for this property right away,
            so clients will see it (and its site visit date) immediately — even before
            any answers are filled in.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => createSurvey.mutate()}>
            Start survey
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-destructive">Failed to load site surveys. Please try again.</p>
      </div>
    );
  }

  if (!surveys || surveys.length === 0) {
    return (
      <div className="text-center py-12">
        <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-sm font-medium text-foreground mb-1">No Site Surveys</h3>
        <p className="text-xs text-muted-foreground mb-4">
          No site survey data has been recorded for <span className="font-medium">{propertyName}</span>.
        </p>
        <div className="flex justify-center">{actionButtons}</div>
        {confirmDialog}
        <TemplatePreviewDialog projectId={projectId} open={previewOpen} onOpenChange={setPreviewOpen} />
      </div>
    );
  }

  // Count fields in a survey data blob
  function countFields(data: any): number {
    if (!data || !Array.isArray(data)) return 0;
    let count = 0;
    for (const field of data) {
      if (field.type === "node" && Array.isArray(field.fields)) {
        count += countFields(field.fields);
      } else {
        count++;
      }
    }
    return count;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Found <span className="font-medium text-foreground">{surveys.length}</span> site survey{surveys.length !== 1 ? "s" : ""} for{" "}
          <span className="font-medium text-foreground">{propertyName}</span>
        </p>
        {actionButtons}
      </div>

      <div className="grid gap-3">
        {surveys.map((survey, i) => {
          const parsedData = typeof survey.data === "string" ? JSON.parse(survey.data) : survey.data;
          const fieldCount = countFields(parsedData);
          const topLevelCount = Array.isArray(parsedData) ? parsedData.length : 0;
          const date = new Date(survey.captured_at);

          return (
            <button
              key={survey.id}
              onClick={() => onSelect(survey)}
              className="group text-left animate-fade-in"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <Card className="transition-all duration-200 hover:border-primary/40 cursor-pointer">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <ClipboardList className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">
                      Site Visit — {date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {topLevelCount} sections · {fieldCount} fields
                      </Badge>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>

      {confirmDialog}
      <TemplatePreviewDialog projectId={projectId} open={previewOpen} onOpenChange={setPreviewOpen} />
      <MergeSurveysDialog
        projectId={projectId}
        propertyId={propertyId}
        surveys={surveys ?? []}
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        onMerged={onSelect}
      />
    </div>
  );
}
